import React from "react";
import {
  Box,
  Typography,
  Button,
  CssBaseline
} from "@mui/material";
import { useHistory } from "react-router-dom";

const SUPPORT_WHITE = "#FFFDF8";
const SUPPORT_GRAY = "#B8B8B8";
const SUPPORT_MUTED = "#777777";

function SupportPage() {
  const history = useHistory();

  const sectionTitleSx = {
    fontSize: {
      xs: "1.35rem",
      md: "1.6rem"
    },
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: SUPPORT_WHITE,
    mb: 2
  };

  const bodySx = {
    fontSize: {
      xs: "0.95rem",
      md: "1rem"
    },
    lineHeight: 1.8,
    fontWeight: 300,
    color: SUPPORT_GRAY,
    mb: 2.5
  };

  const supportCardSx = {
    border: "1px solid #292929",
    borderRadius: "14px",
    p: {
      xs: 3,
      md: 4
    },
    bgcolor: "#080808",
    transition: "border-color 180ms ease",

    "&:hover": {
      borderColor: "#444"
    }
  };

  const supportButtonSx = {
    mt: 2,
    color: SUPPORT_WHITE,
    borderColor: "#444",
    textTransform: "none",
    fontWeight: 300,
    px: 2.5,

    "&:hover": {
      borderColor: "#777",
      bgcolor: "#111"
    }
  };

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          color: SUPPORT_WHITE
        }}
      >
        {/* =====================================================
            HEADER
            ===================================================== */}

        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,

            bgcolor: "rgba(0, 0, 0, 0.94)",
            backdropFilter: "blur(12px)",

            borderBottom: "1px solid #222",

            px: {
              xs: 3,
              md: 6
            },

            py: 2.5,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          {/* Logo */}

          <Box
            component="button"
            type="button"
            onClick={() => history.push("/")}
            aria-label="Return to Colloquia"
            sx={{
              border: 0,
              background: "transparent",
              padding: 0,
              cursor: "pointer",

              display: "flex",
              alignItems: "center"
            }}
          >
            <Box
              component="img"
              src="/Colloquia_Logo.png"
              alt="Colloquia"
              sx={{
                height: {
                  xs: 55,
                  md: 65
                },

                width: "auto",

                borderRadius: "12px"
              }}
            />
          </Box>

          {/* Return button */}

          <Button
            onClick={() => history.push("/")}
            sx={{
              color: SUPPORT_GRAY,
              fontWeight: 300,
              textTransform: "none",
              fontSize: "0.9rem",

              "&:hover": {
                color: SUPPORT_WHITE,
                bgcolor: "transparent"
              }
            }}
          >
            Return to Colloquia
          </Button>
        </Box>

        {/* =====================================================
            MAIN CONTENT
            ===================================================== */}

        <Box
          component="main"
          sx={{
            width: "100%",
            maxWidth: 920,

            mx: "auto",

            px: {
              xs: 4,
              sm: 6,
              md: 8
            },

            pt: {
              xs: 8,
              md: 12
            },

            pb: {
              xs: 10,
              md: 14
            }
          }}
        >
          {/* =====================================================
              TITLE
              ===================================================== */}

          <Box
            sx={{
              mb: {
                xs: 7,
                md: 9
              }
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "2.5rem",
                  sm: "3.2rem",
                  md: "4rem"
                },

                lineHeight: 1.1,
                fontWeight: 300,
                letterSpacing: "-0.035em",

                color: SUPPORT_WHITE,

                mb: 2
              }}
            >
              Support
            </Typography>

            <Typography
              sx={{
                maxWidth: 700,

                fontSize: {
                  xs: "1rem",
                  md: "1.1rem"
                },

                lineHeight: 1.75,
                fontWeight: 300,

                color: SUPPORT_GRAY
              }}
            >
              We're here to help with your Colloquia account,
              technical issues, safety concerns, and questions
              about the platform.
            </Typography>
          </Box>

          {/* =====================================================
              SUPPORT OPTIONS
              ===================================================== */}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr"
              },

              gap: 2,

              mb: 9
            }}
          >
            {/* ACCOUNT */}

            <Box sx={supportCardSx}>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 400,
                  color: SUPPORT_WHITE,
                  mb: 1.5
                }}
              >
                Account & Login
              </Typography>

              <Typography sx={bodySx}>
                Having trouble signing in, creating an account,
                verifying your email address, or accessing your
                account?
              </Typography>

              <Button
                variant="outlined"
                onClick={() => history.push("/contact")}
                sx={supportButtonSx}
              >
                Get Account Help
              </Button>
            </Box>

            {/* TECHNICAL */}

            <Box sx={supportCardSx}>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 400,
                  color: SUPPORT_WHITE,
                  mb: 1.5
                }}
              >
                Technical Issues
              </Typography>

              <Typography sx={bodySx}>
                Tell us about bugs, broken pages, unexpected
                behavior, or other technical problems you encounter
                while using Colloquia.
              </Typography>

              <Button
                variant="outlined"
                onClick={() => history.push("/contact")}
                sx={supportButtonSx}
              >
                Report a Problem
              </Button>
            </Box>

            {/* SAFETY */}

            <Box sx={supportCardSx}>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 400,
                  color: SUPPORT_WHITE,
                  mb: 1.5
                }}
              >
                Safety & Content
              </Typography>

              <Typography sx={bodySx}>
                If you encounter content or behavior that violates
                Colloquia's rules, please let us know so that we can
                review it.
              </Typography>

              <Button
                variant="outlined"
                onClick={() => history.push("/contact")}
                sx={supportButtonSx}
              >
                Report an Issue
              </Button>
            </Box>

            {/* PRIVACY */}

            <Box sx={supportCardSx}>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 400,
                  color: SUPPORT_WHITE,
                  mb: 1.5
                }}
              >
                Privacy
              </Typography>

              <Typography sx={bodySx}>
                Questions about your personal information,
                privacy, data handling, or your rights as a user?
              </Typography>

              <Button
                variant="outlined"
                onClick={() => history.push("/privacy")}
                sx={supportButtonSx}
              >
                Privacy Information
              </Button>
            </Box>
          </Box>

          {/* =====================================================
              BEFORE CONTACTING US
              ===================================================== */}

          <Box sx={{ mb: 8 }}>
            <Typography sx={sectionTitleSx}>
              Before contacting support
            </Typography>

            <Typography sx={bodySx}>
              If you're experiencing a problem with Colloquia,
              please include as much relevant information as you
              can. This helps us investigate the issue more
              efficiently.
            </Typography>

            <Box
              component="ul"
              sx={{
                pl: 3,
                mb: 3
              }}
            >
              {[
                "The email address or username associated with your account.",
                "A description of what happened.",
                "The page or feature where the problem occurred.",
                "The approximate time the problem occurred.",
                "The device and browser you were using.",
                "Screenshots or other information that may help reproduce the problem."
              ].map((item, index) => (
                <Typography
                  component="li"
                  key={index}
                  sx={{
                    ...bodySx,
                    mb: 1.25
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>

            <Typography sx={bodySx}>
              Please do not send us your password or other
              authentication credentials.
            </Typography>
          </Box>

          {/* =====================================================
              ACCOUNT SECURITY
              ===================================================== */}

          <Box sx={{ mb: 8 }}>
            <Typography sx={sectionTitleSx}>
              Account security
            </Typography>

            <Typography sx={bodySx}>
              If you believe that someone has gained unauthorized
              access to your account, contact us as soon as
              possible.
            </Typography>

            <Typography sx={bodySx}>
              Never share your Colloquia password with another
              person, including someone claiming to be a member of
              the Colloquia team.
            </Typography>

            <Typography sx={bodySx}>
              Colloquia will not ask you to provide your password
              through email or through a support request.
            </Typography>
          </Box>

          {/* =====================================================
              PRIVACY
              ===================================================== */}

          <Box sx={{ mb: 8 }}>
            <Typography sx={sectionTitleSx}>
              Privacy requests
            </Typography>

            <Typography sx={bodySx}>
              If your question concerns your personal information,
              please review our Privacy Policy for information about
              how Colloquia collects and uses information.
            </Typography>

            <Button
              variant="outlined"
              onClick={() => history.push("/privacy")}
              sx={supportButtonSx}
            >
              Read Privacy Policy
            </Button>
          </Box>

          {/* =====================================================
              GENERAL QUESTIONS
              ===================================================== */}

          <Box sx={{ mb: 9 }}>
            <Typography sx={sectionTitleSx}>
              General questions
            </Typography>

            <Typography sx={bodySx}>
              For questions about Colloquia, partnerships, feedback,
              or anything else that does not fit into the categories
              above, please contact us.
            </Typography>

            <Button
              variant="outlined"
              onClick={() => history.push("/contact")}
              sx={{
                ...supportButtonSx,
                borderColor: "#FFFDF8"
              }}
            >
              Contact Colloquia
            </Button>
          </Box>

          {/* =====================================================
              FOOTER NAVIGATION
              ===================================================== */}

          <Box
            sx={{
              borderTop: "1px solid #222",

              pt: 4,

              display: "flex",
              flexWrap: "wrap",

              gap: {
                xs: 0.5,
                md: 1
              }
            }}
          >
            {[
              ["About", "/about"],
              ["Terms", "/terms"],
              ["Privacy", "/privacy"],
              ["Careers", "/careers"],
              ["Support", "/support"],
              ["Contact", "/contact"]
            ].map(([label, path]) => (
              <Button
                key={label}
                onClick={() => history.push(path)}
                sx={{
                  color:
                    label === "Support"
                      ? SUPPORT_WHITE
                      : SUPPORT_MUTED,

                  textTransform: "none",
                  fontWeight: 300,
                  fontSize: "0.9rem",

                  minWidth: "auto",
                  px: 1,

                  "&:hover": {
                    color: SUPPORT_WHITE,
                    bgcolor: "transparent"
                  }
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Typography
            sx={{
              mt: 3,
              color: "#555",
              fontSize: "0.8rem",
              fontWeight: 300
            }}
          >
            © 2026 Colloquia from Penneferies Squire Capital
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default SupportPage;